/*
 * The spotlight on VISITORS. While it is on, the grid's root carries `funnel-spot` and the stylesheet dims every element that carries a visitor_id (the bubble, its icon, its pulses and
 * badges: "everything tied to a visitor carries a visitor_id attribute", clusterService.ts) EXCEPT those marked `funnel-bubble-lit`. Dim-by-default is deliberate: a bubble that has only
 * just appeared is dim until it is judged, never lit-then-dimmed (a flash of the wrong thing), and only the lit few need touching. A class, not an inline opacity: the idle and typing
 * badges run opacity animations, which override an inline style but not an !important stylesheet rule, and `transition` is never touched (the app eases bubble movement with it).
 */
export const setSpotClass = (svg: any, on: boolean) => {
  const node = svg?.node?.();
  if (node && node.classList.contains("funnel-spot") !== on) node.classList.toggle("funnel-spot", on);
};

/**
 * WHO IS LIT: a visitor who has ENTERED the funnel and is standing on a step they have REACHED (funnelProgress.ts: `k` steps reached, so the first `k` pages of the funnel). Three things are
 * therefore dim, all of them "not funnel activity": someone who never entered; someone who entered and has wandered to a page that is not part of the funnel (in Loose they are still IN
 * PLAY in the numbers); and someone standing on a funnel page they have NOT reached yet, because they skipped a step (Chris, 2026-10-18: a visitor went / then /shop/boots, /product/trail-boot,
 * /cart and never /shop, so by the numbers they are still at Step 1, yet they were lit standing in the Step 5 box). They light up when they reach the step they are on (the next step in order).
 * Measured beforehand: nothing that should be dim failed to dim; what was bright and should not have been was exactly these.
 */
export interface Reached {
  k: number;
  broken: boolean;
}
export const isLitAt = (state: Reached | undefined, strict: boolean, page: string | undefined, funnelPages: string[]): boolean => {
  if (!state || state.k < 1 || (strict && state.broken) || !page) return false;
  const at = funnelPages.indexOf(page);

  return at >= 0 && at < state.k;
};

export const litVisitors = (bubbles: Array<{ id: string; page: string }>, stateOf: (id: string) => Reached | undefined, strict: boolean, funnelPages: string[]): Set<string> =>
  new Set(bubbles.filter((b) => isLitAt(stateOf(b.id), strict, b.page, funnelPages)).map((b) => b.id));

/**
 * Should an EFFECT that belongs to a visitor be dimmed with them? The bursts when a visitor arrives and when they close into the dock, and the confetti when they convert, are not
 * tied to the visitor's own elements (they are drawn on their own), so the visitor rule in the stylesheet never reached them: a dimmed visitor closed in a bright burst of colour
 * (Chris, 2026-10-18; measured: 127 dock bursts in a minute, none dimmed). The same test as `litVisitors` (isLitAt), and only while the spotlight is on; a visitor who is LEAVING is always dimmed.
 */
export const dimsEffects = (spotlightOn: boolean, state: Reached | undefined, strict: boolean, page: string | undefined, funnelPages: string[], leaving = false): boolean =>
  spotlightOn && (leaving || !isLitAt(state, strict, page, funnelPages));

/** The moment a visitor starts to LEAVE (their bubble sets off for the dock): dim everything of theirs at once, without waiting for the next pass (up to a quarter of a second). */
export const dimVisitorNow = (svg: any, visitorId: string) => {
  const node = svg?.node?.();
  if (!node || !node.classList.contains("funnel-spot")) return;
  node.querySelectorAll(`[visitor_id="${visitorId}"]`).forEach((el: Element) => el.classList.remove("funnel-bubble-lit"));
};

/**
 * `lit` is who is lit (litVisitors); every other element that carries a visitor_id is dimmed. That includes a visitor who is LEAVING: their bubble is flying off to the dock, no longer on
 * any page, and is no longer among the grid's bubbles, so it is not in `lit` (Chris, 2026-10-18: "Any bubble or icon leaving/closing should be dimmed in dim mode"; an earlier version
 * kept a leaving funnel visitor lit, and that was wrong). Touches an element only when its state changes, for the same reason as the picking class.
 */
export const applyBubbles = (svg: any, lit: Set<string> | null) => {
  const node = svg?.node?.();
  if (!node) return;
  if (!lit) {
    setSpotClass(svg, false);
    node.querySelectorAll(".funnel-bubble-lit").forEach((el: Element) => el.classList.remove("funnel-bubble-lit"));

    return;
  }
  setSpotClass(svg, true);
  node.querySelectorAll("[visitor_id]").forEach((el: Element) => {
    const on = lit.has(el.getAttribute("visitor_id") || "");
    if (el.classList.contains("funnel-bubble-lit") !== on) el.classList.toggle("funnel-bubble-lit", on);
  });
};
